import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import swal from "sweetalert";
import axios from "axios";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import PageHeading from "../../Components/PageHeading/PageHeading";
import AiFactsDisplay from "../../Components/AiFacts/AiFactsDisplay";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { AdVideoExamplePrompt } from "./AdVideoExamplePrompt";

import "./NewAdVideo.css";

const API_BASE = "https://backend.whalesai.com/api/v1";
const AD_VIDEO_API = `${API_BASE}/advertisement-video-ai`;

const ACTIVE_STORAGE_KEY = "active_ad_video_ai";
const UNVIEWED_STORAGE_KEY = "unviewed_ad_video_ai";

const formQuestions = [
    {
        key: "promoting",
        label: "What are you promoting?",
        placeholder: "e.g. Smart Home Automation System",
        required: true,
    },
    {
        key: "targetAudience",
        label: "Who is your target audience?",
        placeholder: "e.g. Busy professionals and families",
        required: true,
    },
    {
        key: "keyMessage",
        label: "What is the key message?",
        placeholder: "e.g. Control your home with your voice",
        required: true,
    },
    {
        key: "action",
        label: "What action should viewers take?",
        placeholder: "e.g. Visit our website for a free demo",
        required: true,
    },
    {
        key: "visualStyle",
        label: "What visual style do you want?",
        placeholder: "e.g. Futuristic, cinematic and cozy",
        required: true,
    },
    {
        key: "specialInstructions",
        label: "Any special instructions?",
        placeholder: "e.g. Focus on family interactions",
        required: false,
        isTextarea: true,
    },
];

const initialFormState = {
    promoting: "",
    targetAudience: "",
    keyMessage: "",
    action: "",
    visualStyle: "",
    specialInstructions: "",
};

const formatKey = (key = "") =>
    String(key)
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim();

const getToken = () => localStorage.getItem("aiUser");

const apiHeaders = () => ({
    authorization: `Bearer ${getToken()}`,
});

const debugResponse = (label, response) => {
    console.groupCollapsed(`%c[AD VIDEO AI] ${label}`, "color:#22d3ee;font-weight:700;");
    console.log("Status:", response?.status);
    console.log("Success:", response?.data?.success);
    console.log("Message:", response?.data?.message);
    console.log("Transaction ID:", response?.data?.transactionId);
    console.log("Response:", response?.data);
    console.groupEnd();
};

const debugError = (label, error) => {
    console.groupCollapsed(`%c[AD VIDEO AI] ${label}`, "color:#ef4444;font-weight:700;");
    console.error("Message:", error?.message);
    console.error("Response:", error?.response?.data);
    console.error("Status:", error?.response?.status);
    console.error("Full Error:", error);
    console.groupEnd();
};

function LoadingState({ transactionId }) {
    return (
        <div className="va-progress-card advideo-loading-card">
            <div className="va-progress-orb">
                <AutoAwesomeIcon />
            </div>

            <p className="va-progress-title">
                AI is Creating Your Advertisement
            </p>

            <p className="va-progress-text">
                Your Advertisement is being generated in the background.
                You can safely return later. Once ready, it will
                also be available from your Usage page.
            </p>

            <div className="va-ai-facts">
                <AiFactsDisplay />
            </div>

        </div>
    );
}

function EmptyState() {
    return (
        <div className="advideo-empty-state">
            <div className="advideo-empty-icon">✦</div>

            <h3>Your advertisement blueprint will appear here</h3>

            <p>
                Answer the questions above and let AI build the complete
                advertisement structure for you.
            </p>

            <div className="advideo-empty-features">
                <span>Character Bible</span>
                <span>Location Bible</span>
                <span>Story Structure</span>
                <span>Scene Breakdown</span>
                <span>Production Prompts</span>
            </div>
        </div>
    );
}

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text || "");

            setCopied(true);

            setTimeout(() => {
                setCopied(false);
            }, 1800);
        } catch (error) {
            console.error("[AD VIDEO AI] Copy failed:", error);
        }
    };

    return (
        <button
            type="button"
            className="advideo-copy-btn"
            onClick={copy}
        >
            {copied ? "✓ Copied" : "Copy"}
        </button>
    );
}

function CharacterBible({
    characters,
    onGenerate,
    generating,
    transactionId,
    characterImage,
    locationImage,
}) {
    if (!characters) return null;

    const charList = Array.isArray(characters)
        ? characters
        : Object.entries(characters).map(([key, value]) => ({
            ...(value || {}),
            _key: key,
        }));

    if (!charList.length) return null;

    return (
        <div className="advideo-section-card">
            <div className="advideo-section-heading">
                <div>
                    <span className="advideo-section-kicker">
                        PRODUCTION
                    </span>

                    {/* <h3>Character Bible</h3> */}
                    <div className="d-flex justify-content-between align-items-center">
                        <h3>Character Bible</h3>
                    </div>
                </div>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => onGenerate("character")}
                // disabled={generating || !transactionId}
                >
                    {generating ? "Generating..." : "Generate Character"}
                </Button>
            </div>

            <div className="advideo-card-grid">
                {charList.map((character, index) => {
                    const name =
                        character?.character_name ||
                        character?.name ||
                        character?._key ||
                        `Character ${index + 1}`;

                    return (
                        <div
                            className="advideo-character-card"
                            key={character?._key || index}
                        >
                            <div className="advideo-character-top">
                                <div className="advideo-avatar">
                                    {name.charAt(0).toUpperCase()}
                                </div>

                                <div>
                                    <h4>{name}</h4>
                                    <span>Character {index + 1}</span>
                                </div>
                            </div>

                            <div className="advideo-detail-list">
                                {Object.entries(character || {})
                                    .filter(
                                        ([key]) =>
                                            ![
                                                "character_name",
                                                "name",
                                                "_key",
                                            ].includes(key)
                                    )
                                    .map(([key, value]) => (
                                        <div
                                            className="advideo-detail-row"
                                            key={key}
                                        >
                                            <span>
                                                {formatKey(key)}
                                            </span>

                                            <strong>
                                                {typeof value === "object"
                                                    ? JSON.stringify(value)
                                                    : String(value ?? "—")}
                                            </strong>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {generating && (
                <div className="text-center mt-4">
                    <div
                        className="spinner-border text-light"
                        role="status"
                    >
                        <span className="visually-hidden">
                            Generating...
                        </span>
                    </div>

                    <div className="text-warning mt-2">
                        Generating image...
                    </div>
                </div>
            )}

            {characterImage && (
                <div className="mt-4">
                    <img
                        src={characterImage}
                        alt="Generated Character"
                        className="img-fluid rounded"
                        style={{
                            width: "100%",
                            display: "block",
                        }}
                    />
                </div>
            )}

        </div>
    );
}

function LocationBible({
    locations,
    onGenerate,
    generating,
    transactionId,
    locationImage,
}) {
    if (!locations) return null;

    let locationList = [];

    if (Array.isArray(locations)) {
        locationList = locations.map((location, index) => ({
            ...location,
            _index: index,
        }));
    } else {
        const primary = locations.primary_locations || {};
        const supporting = locations.supporting_locations || {};

        Object.entries(primary).forEach(([key, value]) => {
            locationList.push({
                ...(value || {}),
                _key: key,
                _type: "Primary",
            });
        });

        Object.entries(supporting).forEach(([key, value]) => {
            locationList.push({
                ...(value || {}),
                _key: key,
                _type: "Supporting",
            });
        });
    }

    if (!locationList.length) return null;

    return (
        <div className="advideo-section-card">
            <div className="advideo-section-heading">
                <div>
                    <span className="advideo-section-kicker">
                        PRODUCTION
                    </span>

                    {/* <h3>Location Bible</h3> */}
                    <div className="d-flex justify-content-between align-items-center">
                        <h3>Location Bible</h3>
                    </div>
                </div>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => onGenerate("location")}
                // disabled={generating || !transactionId}
                >
                    {generating ? "Generating..." : "Generate Location"}
                </Button>
            </div>

            <div className="advideo-card-grid">
                {locationList.map((location, index) => {
                    const name =
                        location?.location_name ||
                        location?.name ||
                        location?._key ||
                        `Location ${index + 1}`;

                    const type =
                        location?.type ||
                        location?.location_type ||
                        location?._type;

                    return (
                        <div
                            className="advideo-location-card"
                            key={location?._key || location?._index || index}
                        >
                            <div className="advideo-location-top">
                                <div className="advideo-location-icon">
                                    ◉
                                </div>

                                <div>
                                    <h4>{name}</h4>

                                    {type && (
                                        <span
                                            className={`advideo-location-type ${type.toLowerCase() ===
                                                "primary"
                                                ? "primary"
                                                : "supporting"
                                                }`}
                                        >
                                            {type}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="advideo-detail-list">
                                {Object.entries(location || {})
                                    .filter(
                                        ([key]) =>
                                            ![
                                                "location_name",
                                                "name",
                                                "type",
                                                "location_type",
                                                "_key",
                                                "_index",
                                                "_type",
                                            ].includes(key)
                                    )
                                    .map(([key, value]) => (
                                        <div
                                            className="advideo-detail-row"
                                            key={key}
                                        >
                                            <span>
                                                {formatKey(key)}
                                            </span>

                                            <strong>
                                                {typeof value === "object"
                                                    ? JSON.stringify(value)
                                                    : String(value ?? "—")}
                                            </strong>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {generating && (
                <div className="text-center mt-4">
                    <div
                        className="spinner-border text-light"
                        role="status"
                    >
                        <span className="visually-hidden">
                            Generating
                        </span>
                    </div>

                    <div className="text-warning mt-2">
                        Generating image
                    </div>
                </div>
            )}

            {locationImage && (
                <div className="mt-4">
                    <img
                        src={locationImage}
                        alt="Generated Location"
                        className="img-fluid rounded"
                        style={{
                            width: "100%",
                            display: "block",
                        }}
                    />
                </div>
            )}
        </div>
    );
}

function StoryStructure({ story }) {
    if (!story) return null;

    return (
        <div className="advideo-section-card">
            <div className="advideo-section-heading">
                <div>
                    <span className="advideo-section-kicker">
                        NARRATIVE
                    </span>

                    <h3>Story Structure</h3>
                </div>
            </div>

            <div className="advideo-story-grid">
                {Object.entries(story).map(([key, value]) => (
                    <div className="advideo-story-item" key={key}>
                        <span>{formatKey(key)}</span>

                        <div>
                            {typeof value === "object"
                                ? JSON.stringify(value, null, 2)
                                : String(value ?? "—")}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SceneCard({ scene, production = false }) {
    const displayFields = Object.entries(scene || {}).filter(
        ([key]) =>
            !["scene_number", "scene_title"].includes(key)
    );

    const sceneText = displayFields
        .map(
            ([key, value]) =>
                `${formatKey(key)}: ${typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value ?? "")
                }`
        )
        .join("\n\n");

    return (
        <div className="advideo-scene-card">
            <div className="advideo-scene-header">
                <div className="advideo-scene-number">
                    {scene?.scene_number || "—"}
                </div>

                <div className="advideo-scene-heading">
                    <h4>
                        {scene?.scene_title || "Untitled Scene"}
                    </h4>

                    {scene?.duration_seconds && (
                        <span>
                            {scene.duration_seconds}s
                        </span>
                    )}
                </div>

                <CopyButton
                    text={`Scene ${scene?.scene_number || ""
                        } — ${scene?.scene_title || ""
                        }\n\n${sceneText}`}
                />
            </div>

            <div className="advideo-scene-body">
                {displayFields.map(([key, value]) => (
                    <div
                        className="advideo-scene-field"
                        key={key}
                    >
                        <div className="advideo-scene-field-label">
                            {formatKey(key)}
                        </div>

                        <div
                            className={
                                production
                                    ? "advideo-scene-field-value production"
                                    : "advideo-scene-field-value"
                            }
                        >
                            {typeof value === "object"
                                ? JSON.stringify(
                                    value,
                                    null,
                                    2
                                )
                                : String(value ?? "—")}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SceneBreakdown({ scenes }) {
    if (!Array.isArray(scenes) || !scenes.length) {
        return (
            <div className="advideo-no-data">
                No scene breakdown available.
            </div>
        );
    }

    return (
        <div className="advideo-scenes-list">
            {scenes.map((scene, index) => (
                <SceneCard
                    key={
                        scene?.scene_number ||
                        index
                    }
                    scene={{
                        scene_number:
                            scene?.scene_number ||
                            index + 1,
                        scene_title:
                            scene?.scene_title ||
                            `Scene ${index + 1}`,
                        duration_seconds:
                            scene?.duration_seconds,
                        character_actions:
                            scene?.character_actions,
                    }}
                />
            ))}
        </div>
    );
}

function ProductionPrompts({ scenes }) {
    if (!Array.isArray(scenes) || !scenes.length) {
        return (
            <div className="advideo-no-data">
                No production prompts available.
            </div>
        );
    }

    return (
        <div className="advideo-scenes-list">
            {scenes.map((scene, index) => (
                <SceneCard
                    key={
                        scene?.scene_number ||
                        index
                    }
                    production
                    scene={{
                        scene_number:
                            scene?.scene_number ||
                            index + 1,
                        scene_title:
                            scene?.scene_title ||
                            `Scene ${index + 1}`,
                        duration_seconds:
                            scene?.duration_seconds,
                        video_prompt:
                            scene?.video_prompt,
                    }}
                />
            ))}
        </div>
    );
}

function PromptCollection({
    title,
    prompts,
    type = "prompt",
}) {
    if (!Array.isArray(prompts) || !prompts.length) {
        return null;
    }

    return (
        <div className="advideo-section-card">
            <div className="advideo-section-heading">
                <div>
                    <span className="advideo-section-kicker">
                        AI GENERATED
                    </span>

                    <h3>{title}</h3>
                </div>

                <span className="advideo-count-badge">
                    {prompts.length} Prompts
                </span>
            </div>

            <div className="advideo-prompt-list">
                {prompts.map((prompt, index) => {
                    const text =
                        typeof prompt === "string"
                            ? prompt
                            : JSON.stringify(
                                prompt,
                                null,
                                2
                            );

                    return (
                        <div
                            className="advideo-prompt-card"
                            key={index}
                        >
                            <div className="advideo-prompt-number">
                                {String(index + 1).padStart(
                                    2,
                                    "0"
                                )}
                            </div>

                            <div className="advideo-prompt-content">
                                <div className="advideo-prompt-top">
                                    <span>
                                        {formatKey(type)}{" "}
                                        Prompt
                                    </span>

                                    <CopyButton
                                        text={text}
                                    />
                                </div>

                                <pre>{text}</pre>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ResultDashboard({
    resultData,
    onGenerateBibleImage,
    generatingCharacter,
    generatingLocation,
    transactionId,
    characterImage,
    locationImage,
}) {
    const [activeTab, setActiveTab] = useState("characters");

    const claudeData = resultData?.claudePrompt || {};
    const gptData = resultData?.gptPrompt || {};

    const characters =
        claudeData.characters ||
        claudeData.character_bible ||
        gptData.characters;

    const locations =
        claudeData.locations ||
        claudeData.location_bible ||
        gptData.locations;

    const story =
        claudeData.story_structure ||
        gptData.story_structure;

    const scenes = claudeData.scenes || [];
    const productionScenes = gptData.scenes || [];

    const tabs = [
        {
            key: "characters",
            label: "Character Bible",
            available: !!characters,
        },
        {
            key: "locations",
            label: "Location Bible",
            available: !!locations,
        },
        {
            key: "story",
            label: "Story Structure",
            available: !!story,
        },
        {
            key: "scenes",
            label: "Scene Breakdown",
            available: scenes.length > 0,
        },
        {
            key: "prompts",
            label: "Production Prompts",
            available: productionScenes.length > 0,
        },
        {
            key: "character-prompts",
            label: "Character Prompts",
            available:
                Array.isArray(
                    resultData?.character_image_prompts
                ) &&
                resultData.character_image_prompts.length >
                0,
        },
        {
            key: "location-prompts",
            label: "Location Prompts",
            available:
                Array.isArray(
                    resultData?.location_image_prompts
                ) &&
                resultData.location_image_prompts.length >
                0,
        },
    ].filter((tab) => tab.available);

    useEffect(() => {
        if (
            tabs.length &&
            !tabs.some(
                (tab) => tab.key === activeTab
            )
        ) {
            setActiveTab(tabs[0].key);
        }
    }, [resultData]);

    const title =
        claudeData.video_title ||
        gptData.video_title ||
        "Generated Advertisement";

    const duration =
        claudeData.total_duration_seconds ||
        gptData.total_duration_seconds;

    return (
        <div className="advideo-result-container">
            <div className="advideo-result-hero">
                <div>
                    <span className="advideo-result-kicker">
                        ADVERTISEMENT BLUEPRINT
                    </span>

                    <h2>{title}</h2>

                    {duration && (
                        <p>
                            Complete production plan ·{" "}
                            {duration} seconds
                        </p>
                    )}
                </div>

                <div className="advideo-status-pill">
                    <span />
                    Generation Complete
                </div>
            </div>

            {tabs.length > 0 && (
                <div className="advideo-tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={
                                activeTab === tab.key
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setActiveTab(tab.key)
                            }
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="advideo-tab-content">
                {activeTab === "characters" && (
                    <CharacterBible
                        characters={characters}
                        onGenerate={onGenerateBibleImage}
                        generating={generatingCharacter}
                        transactionId={transactionId}
                        characterImage={characterImage}
                        locationImage={locationImage}
                    />
                )}

                {activeTab === "locations" && (
                    <LocationBible
                        locations={locations}
                        onGenerate={onGenerateBibleImage}
                        generating={generatingLocation}
                        transactionId={transactionId}
                        locationImage={locationImage}
                    />
                )}

                {activeTab === "story" && (
                    <StoryStructure story={story} />
                )}

                {activeTab === "scenes" && (
                    <SceneBreakdown scenes={scenes} />
                )}


                {activeTab === "prompts" && (
                    <ProductionPrompts
                        scenes={productionScenes}
                    />
                )}

                {activeTab ===
                    "character-prompts" && (
                        <PromptCollection
                            title="Character Image Prompts"
                            prompts={
                                resultData.character_image_prompts
                            }
                            type="Character"
                        />
                    )}

                {activeTab ===
                    "location-prompts" && (
                        <PromptCollection
                            title="Location Image Prompts"
                            prompts={
                                resultData.location_image_prompts
                            }
                            type="Location"
                        />
                    )}
            </div>
        </div>
    );
}

function SessionsPanel({
    sessions,
    loading,
    onSelect,
}) {
    if (!sessions?.length && !loading) {
        return null;
    }

    return (
        <div className="advideo-sessions-card">
            <div className="advideo-section-heading">
                <div>
                    <span className="advideo-section-kicker">
                        HISTORY
                    </span>

                    <h3>Previous Advertisements</h3>
                </div>

                {loading && (
                    <div className="spinner-border spinner-border-sm text-light" />
                )}
            </div>

            <div className="advideo-session-list">
                {sessions.map((session, index) => {
                    const sessionId =
                        session?.sessionId ||
                        session?._id ||
                        session?.id;
                    // setSessionId(sessionId);

                    const title =
                        session?.video_title ||
                        session?.videoTitle ||
                        session?.title ||
                        session?.session?.video_title ||
                        `Advertisement ${index + 1}`;

                    return (
                        <button
                            type="button"
                            className="advideo-session-item"
                            key={sessionId || index}
                            onClick={() =>
                                onSelect(sessionId)
                            }
                        >
                            <div className="advideo-session-icon">
                                ▶
                            </div>

                            <div>
                                <strong>{title}</strong>

                                <span>
                                    {sessionId ||
                                        "View session"}
                                </span>
                            </div>

                            <span className="advideo-session-arrow">
                                →
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function NewAdVideo() {
    const { AIuser } =
        useContext(UserAuthContext);

    const navigate = useNavigate();

    const [formData, setFormData] =
        useState(initialFormState);

    const [isLoading, setIsLoading] =
        useState(false);

    const [resultData, setResultData] =
        useState(null);

    const [transactionId, setTransactionId] =
        useState(null);

    const [sessions, setSessions] =
        useState([]);

    const [loadingSessions, setLoadingSessions] =
        useState(false);

    const pollingRef = useRef(null);
    const checkingRef = useRef(false);
    const mountedRef = useRef(true);


    const [suggestedPrompt, setSuggestedPrompt] = useState("");
    const [backendSuggestions, setBackendSuggestions] = useState(null);
    const [checkingPrompt, setCheckingPrompt] = useState(false);
    const promptTimeoutRef = useRef(null);

    const [characterImage, setCharacterImage] = useState(null);
    const [locationImage, setLocationImage] = useState(null);

    const [generatingCharacter, setGeneratingCharacter] = useState(false);
    const [generatingLocation, setGeneratingLocation] = useState(false);
    const [sessionId, setSessionId] = useState(null);

    const [imageGeneration, setImageGeneration] = useState({
        character: null,
        location: null,
    });

    console.log("Char", characterImage);
    console.log("LocationImage", locationImage);



    useEffect(() => {

    }, [])

    const creditToLocalString = AIuser?.credits
        ? parseInt(
            Math.floor(AIuser.credits)
        ).toLocaleString()
        : "0";

    const isFormValid =
        formData.promoting.trim() &&
        formData.targetAudience.trim() &&
        formData.keyMessage.trim() &&
        formData.action.trim() &&
        formData.visualStyle.trim();

    // =====================================================
    // GENERIC HELPERS
    // =====================================================

    const clearPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const saveActiveTransaction = (txId) => {
        if (!txId) return;

        localStorage.setItem(
            ACTIVE_STORAGE_KEY,
            JSON.stringify({
                transactionId: txId,
                aiType: "advertisement-video-ai",
                startedAt: Date.now(),
            })
        );
    };

    const removeActiveTransaction = () => {
        localStorage.removeItem(
            ACTIVE_STORAGE_KEY
        );
    };

    // =====================================================
    // MARK VIEWED
    // =====================================================

    const markViewed = async (txId) => {
        if (!txId) return;

        try {
            const response = await axios.post(
                `${AD_VIDEO_API}/mark-viewed`,
                {
                    transactionId: txId,
                },
                {
                    headers: apiHeaders(),
                }
            );

            debugResponse(
                "MARK VIEWED RESPONSE",
                response
            );
        } catch (error) {
            debugError(
                "MARK VIEWED ERROR",
                error
            );
        }
    };

    // =====================================================
    // HANDLE COMPLETED
    // =====================================================

    const handleCompleted = async (
        data,
        source = "active-status"
    ) => {
        if (!data) return;

        console.group(
            `%c[AD VIDEO AI] COMPLETED FROM ${source.toUpperCase()}`,
            "color:#22c55e;font-weight:700;"
        );
        console.log(
            "Completed Data:",
            data
        );
        console.log(
            "Transaction ID:",
            data?.transactionId
        );
        console.log(
            "Session ID:",
            data?.data?.sessionId
        );
        console.log(
            "GPT Prompt:",
            data?.data?.gptPrompt
        );
        console.log(
            "Claude Prompt:",
            data?.data?.claudePrompt
        );
        console.log(
            "Character Image Prompts:",
            data?.data
                ?.character_image_prompts
        );
        console.log(
            "Location Image Prompts:",
            data?.data
                ?.location_image_prompts
        );
        console.log(
            "Full Response:",
            data
        );
        console.groupEnd();

        const completedData =
            data?.data || data;

        if (!completedData) return;

        setResultData(completedData);
        setSessionId(
            completedData?.sessionId ||
            data?.data?.sessionId ||
            null
        );

        const txId =
            data?.transactionId ||
            completedData?.transactionId ||
            transactionId;

        setTransactionId(txId || null);
        setIsLoading(false);

        clearPolling();
        removeActiveTransaction();

        if (txId) {
            await markViewed(txId);
        }

        try {
            localStorage.setItem(
                UNVIEWED_STORAGE_KEY,
                JSON.stringify({
                    transactionId: txId,
                    data: completedData,
                    completedAt: Date.now(),
                })
            );
        } catch (error) {
            console.error(
                "[AD VIDEO AI] Failed to store unviewed result:",
                error
            );
        }

        loadSessions();
    };

    // =====================================================
    // HANDLE FAILED
    // =====================================================

    const handleFailed = (data) => {
        console.group(
            "%c[AD VIDEO AI] GENERATION FAILED",
            "color:#ef4444;font-weight:700;"
        );

        console.log("Failed Data:", data);
        console.log(
            "Transaction ID:",
            data?.transactionId
        );
        console.log(
            "Message:",
            data?.message
        );
        console.log(
            "Error:",
            data?.error
        );

        console.groupEnd();

        clearPolling();
        removeActiveTransaction();

        setIsLoading(false);

        swal({
            title: "Generation Failed",
            text:
                data?.message ||
                data?.error ||
                "Advertisement generation failed.",
            icon: "error",
            button: "Okay",
            className:
                "modal_class_success",
        });
    };

    // =====================================================
    // CHECK ACTIVE STATUS
    // =====================================================

    const checkActiveStatus = async () => {
        if (checkingRef.current) {
            return;
        }

        checkingRef.current = true;

        try {
            const response = await axios.get(
                `${AD_VIDEO_API}/active-status`,
                {
                    headers: apiHeaders(),
                }
            );

            debugResponse(
                "ACTIVE STATUS RESPONSE",
                response
            );

            const data = response?.data;

            if (!data?.success) {
                return;
            }

            if (
                data.status === "completed" ||
                data?.data
            ) {
                await handleCompleted(
                    data,
                    "active-status"
                );

                return;
            }

            if (
                data.status === "failed" ||
                data.status === "error"
            ) {
                handleFailed(data);
                return;
            }

            if (data.isGenerating) {
                setIsLoading(true);

                if (data.transactionId) {
                    setTransactionId(
                        data.transactionId
                    );

                    saveActiveTransaction(
                        data.transactionId
                    );
                }
            }
        } catch (error) {
            debugError(
                "ACTIVE STATUS ERROR",
                error
            );
        } finally {
            checkingRef.current = false;
        }
    };

    // =====================================================
    // START POLLING
    // =====================================================

    const startPolling = () => {
        clearPolling();

        checkActiveStatus();

        pollingRef.current =
            setInterval(() => {
                checkActiveStatus();
            }, 3000);
    };

    // =====================================================
    // LOAD SESSIONS
    // =====================================================

    const loadSessions = async () => {
        try {
            setLoadingSessions(true);

            const response = await axios.get(
                `${AD_VIDEO_API}/sessions`,
                {
                    headers: apiHeaders(),
                }
            );

            debugResponse(
                "SESSIONS RESPONSE",
                response
            );

            if (response?.data?.success) {
                setSessions(
                    response.data.sessions ||
                    []
                );
            }
        } catch (error) {
            debugError(
                "SESSIONS ERROR",
                error
            );
        } finally {
            setLoadingSessions(false);
        }
    };

    // =====================================================
    // LOAD SINGLE SESSION
    // =====================================================

    const loadSession = async (sessionId) => {
        if (!sessionId) return;

        try {
            setIsLoading(true);

            const response = await axios.get(
                `${AD_VIDEO_API}/sessions/${encodeURIComponent(
                    sessionId
                )}`,
                {
                    headers: apiHeaders(),
                }
            );

            debugResponse(
                "SINGLE SESSION RESPONSE",
                response
            );

            if (!response?.data?.success) {
                throw new Error(
                    response?.data?.message ||
                    "Failed to load session."
                );
            }

            const session =
                response.data.session;

            console.group(
                "%c[AD VIDEO AI] LOADED SESSION DATA",
                "color:#a855f7;font-weight:700;"
            );
            console.log(
                "Session ID:",
                sessionId
            );
            console.log(
                "Session:",
                session
            );
            console.groupEnd();

            /*
             * Backend session structure may vary.
             * Try the common structures safely.
             */
            const normalized =
                session?.data ||
                session;

            setResultData(normalized);
            setSessionId(
                session?.sessionId ||
                normalized?.sessionId ||
                sessionId
            );

            setTransactionId(
                session?.transactionId ||
                normalized?.transactionId ||
                null
            );

            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
            });
        } catch (error) {
            debugError(
                "SINGLE SESSION ERROR",
                error
            );

            swal({
                text:
                    error?.response?.data
                        ?.message ||
                    error.message ||
                    "Failed to load session.",
                icon: "error",
                button: "Okay",
                className:
                    "modal_class_success",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // =====================================================
    // GENERATE
    // =====================================================

    const handleGenerate = async () => {
        if (!isFormValid) {
            swal({
                text: "Please fill in all required fields.",
                icon: "warning",
                button: "Okay",
                className:
                    "modal_class_success",
            });

            return;
        }

        try {
            setIsLoading(true);
            setResultData(null);
            setTransactionId(null);

            console.group(
                "%c[AD VIDEO AI] GENERATION REQUEST",
                "color:#f59e0b;font-weight:700;"
            );
            console.log(
                "Request URL:",
                `${AD_VIDEO_API}/generate`
            );
            console.log(
                "Request Body:",
                formData
            );
            console.groupEnd();

            const response = await axios.post(
                `${AD_VIDEO_API}/generate`,
                {
                    promoting:
                        formData.promoting,
                    targetAudience:
                        formData.targetAudience,
                    keyMessage:
                        formData.keyMessage,
                    action: formData.action,
                    visualStyle:
                        formData.visualStyle,
                    specialInstructions:
                        formData.specialInstructions,
                },
                {
                    headers: {
                        ...apiHeaders(),
                        "Content-Type":
                            "application/json",
                    },
                }
            );

            debugResponse(
                "GENERATE RESPONSE",
                response
            );

            const data = response?.data;

            if (
                !response?.status ||
                !data?.success
            ) {
                throw new Error(
                    data?.message ||
                    "Failed to start advertisement generation."
                );
            }

            const txId =
                data?.transactionId;

            if (!txId) {
                throw new Error(
                    "Generation started but no transaction ID was returned."
                );
            }

            setTransactionId(txId);

            saveActiveTransaction(txId);

            /*
             * Important:
             * Generate endpoint does NOT return final data.
             * Final data comes from active-status.
             */
            startPolling();
        } catch (error) {
            debugError(
                "GENERATE ERROR",
                error
            );

            setIsLoading(false);

            swal({
                text:
                    error?.response?.data
                        ?.message ||
                    error.message ||
                    "Something went wrong. Please try again.",
                icon: "error",
                button: "Okay",
                className:
                    "modal_class_success",
            });
        }
    };

    // =====================================================
    // CLEAR
    // =====================================================

    const handleClear = () => {
        setFormData(initialFormState);
        setResultData(null);
        setTransactionId(null);

        clearPolling();
        removeActiveTransaction();
        localStorage.removeItem(
            UNVIEWED_STORAGE_KEY
        );

        setSuggestedPrompt("");
        setBackendSuggestions(null);
        setCheckingPrompt(false);

        if (promptTimeoutRef.current) {
            clearTimeout(promptTimeoutRef.current);
            promptTimeoutRef.current = null;
        }

        setCharacterImage(null);
        setLocationImage(null);
        setGeneratingCharacter(false);
        setGeneratingLocation(false);

        setImageGeneration({
            character: null,
            location: null,
        });

        setSessionId(null);
    };

    // =====================================================
    // INPUT
    // =====================================================

    const handleInputChange = (
        key,
        value
    ) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleGenerateBibleImage = async (type) => {
        console.log(
            "[AD VIDEO AI] Generate image clicked:",
            type
        );

        if (!transactionId || !sessionId) {
            swal({
                title: "Please wait",
                text: "Session is not ready yet.",
                icon: "warning",
            });

            return;
        }

        const isCharacter = type === "character";

        try {
            /*
             * Start loading
             */
            if (isCharacter) {
                setGeneratingCharacter(true);
            } else {
                setGeneratingLocation(true);
            }

            const res = await axios.post(
                `${AD_VIDEO_API}/create-image`,
                {
                    sessionId,
                    type,
                    transactionId,
                },
                {
                    headers: apiHeaders(),
                }
            );

            console.log(
                "[AD VIDEO AI] Create image response:",
                res?.data
            );

            if (!res?.data?.success) {
                throw new Error(
                    res?.data?.message ||
                    "Image generation failed."
                );
            }

            /*
             * IMPORTANT:
             *
             * /create-image returns 202.
             * It does NOT return imageUrl.
             *
             * Example:
             *
             * {
             *   success: true,
             *   transactionId: "...",
             *   sessionId: "..."
             * }
             */

            const imageTransactionId =
                res?.data?.transactionId;

            const imageSessionId =
                res?.data?.sessionId;

            if (!imageTransactionId) {
                throw new Error(
                    "Image generation started but no transaction ID was returned."
                );
            }

            /*
             * Save image generation transaction.
             */
            setImageGeneration((prev) => ({
                ...prev,
                [type]: {
                    transactionId: imageTransactionId,
                    sessionId: imageSessionId,
                },
            }));

            console.log(
                "[AD VIDEO AI] Image generation started:",
                {
                    type,
                    transactionId:
                        imageTransactionId,
                    sessionId:
                        imageSessionId,
                }
            );

            /*
             * DO NOT stop loading here.
             *
             * WebSocket completion event will stop it.
             */
        } catch (error) {
            console.error(
                `[AD VIDEO AI] Generate ${type} image error:`,
                error
            );

            if (isCharacter) {
                setGeneratingCharacter(false);
            } else {
                setGeneratingLocation(false);
            }

            swal({
                title: "Generation Failed",
                text:
                    error?.response?.data?.message ||
                    error?.message ||
                    `Failed to generate ${type} image.`,
                icon: "error",
            });
        }
    };

    // const checkPromotingPrompt = async (value) => {
    //     if (!value?.trim()) {
    //         setBackendSuggestions(null);
    //         return;
    //     }

    //     try {
    //         setCheckingPrompt(true);

    //         const res = await axios.post(
    //             `${API_BASE}/prompt-checker/check`,
    //             {
    //                 prompt: value,
    //                 aiService: "ad-video-ai",
    //             },
    //             {
    //                 headers: apiHeaders(),
    //             }
    //         );

    //         if (res?.data?.success) {
    //             setBackendSuggestions(
    //                 res.data.data.suggested_prompt
    //             );
    //             setSessionId(res.data.data.sessionId);
    //         }
    //     } catch (error) {
    //         console.log(
    //             "[AD VIDEO AI] Prompt checker error:",
    //             error
    //         );
    //     } finally {
    //         setCheckingPrompt(false);
    //     }
    // };


    // ====================================================


    const checkPromotingPrompt = async (value) => {
        const prompt = value?.trim();

        if (!prompt) {
            setBackendSuggestions(null);
            setCheckingPrompt(false);
            return;
        }

        try {
            setCheckingPrompt(true);

            const res = await axios.post(
                `${API_BASE}/prompt-checker/check`,
                {
                    prompt,
                    aiService: "ad-video-ai",
                },
                {
                    headers: apiHeaders(),
                }
            );

            if (res?.data?.success) {
                setBackendSuggestions(
                    res.data.data?.suggested_prompt || null
                );

                // Do not overwrite the advertisement session ID here.
                // Prompt-checker session may be different.
            }
        } catch (error) {
            console.error(
                "[AD VIDEO AI] Prompt checker error:",
                error
            );
        } finally {
            setCheckingPrompt(false);
        }
    };


    useEffect(() => {
        setSuggestedPrompt(
            AdVideoExamplePrompt[
            Math.floor(Math.random() * AdVideoExamplePrompt.length)
            ]
        );

        const intervalId = setInterval(() => {
            setSuggestedPrompt(
                AdVideoExamplePrompt[
                Math.floor(Math.random() * AdVideoExamplePrompt.length)
                ]
            );
        }, 5000);

        return () => clearInterval(intervalId);
    }, []);

    // =====================================================
    // INITIAL STATUS CHECK
    // =====================================================

    useEffect(() => {
        mountedRef.current = true;

        window.scrollTo({
            top: 0,
            behavior: "instant",
        });

        const initialize = async () => {
            console.group(
                "%c[AD VIDEO AI] INITIALIZING",
                "color:#38bdf8;font-weight:700;"
            );

            console.log(
                "Active storage:",
                localStorage.getItem(
                    ACTIVE_STORAGE_KEY
                )
            );

            console.groupEnd();

            /*
             * First check whether there is an
             * active transaction.
             */
            const activeStr =
                localStorage.getItem(
                    ACTIVE_STORAGE_KEY
                );

            if (activeStr) {
                try {
                    const active =
                        JSON.parse(
                            activeStr
                        );

                    if (
                        active?.transactionId
                    ) {
                        setTransactionId(
                            active.transactionId
                        );

                        setIsLoading(true);

                        startPolling();

                        return;
                    }
                } catch (error) {
                    console.error(
                        "[AD VIDEO AI] Invalid active transaction:",
                        error
                    );

                    removeActiveTransaction();
                }
            }

            /*
             * Check backend even when there is
             * no local transaction.
             */
            await checkActiveStatus();

            loadSessions();
        };

        initialize();

        return () => {
            mountedRef.current = false;
            clearPolling();
        };
    }, []);

    // =====================================================
    // BACKEND EVENTS
    // =====================================================

    const handleImageCompleted = (data) => {
        console.group(
            "%c[AD VIDEO AI] IMAGE GENERATION COMPLETED",
            "color:#22c55e;font-weight:700;"
        );

        console.log("Image Event:", data);

        console.groupEnd();

        /*
         * Make sure this is our Advertisement Video AI
         * image completion event.
         */
        if (
            data?.type !==
            "advertisement-video-ai" ||
            data?.action !==
            "ad_video_ai_image_completed"
        ) {
            return;
        }

        const imageData = data?.data;

        const imageType =
            imageData?.imageType;

        const imageUrl =
            imageData?.imageUrl;

        const completedTransactionId =
            imageData?.transactionId;

        console.log(
            "[AD VIDEO AI] Image Type:",
            imageType
        );

        console.log(
            "[AD VIDEO AI] Image URL:",
            imageUrl
        );

        console.log(
            "[AD VIDEO AI] Image Transaction:",
            completedTransactionId
        );

        if (!imageUrl) {
            console.warn(
                "[AD VIDEO AI] imageUrl missing:",
                data
            );

            return;
        }

        if (
            imageType !== "character" &&
            imageType !== "location"
        ) {
            console.warn(
                "[AD VIDEO AI] Unknown imageType:",
                imageType
            );

            return;
        }

        /*
         * Check transaction.
         */
        const currentImageTransaction =
            imageGeneration?.[
                imageType
            ]?.transactionId;

        if (
            currentImageTransaction &&
            completedTransactionId &&
            currentImageTransaction !==
            completedTransactionId
        ) {
            console.warn(
                "[AD VIDEO AI] Image transaction mismatch:",
                {
                    expected:
                        currentImageTransaction,
                    received:
                        completedTransactionId,
                    imageType,
                }
            );

            return;
        }

        /*
         * CHARACTER
         */
        if (imageType === "character") {
            setCharacterImage(imageUrl);

            setGeneratingCharacter(false);

            setImageGeneration((prev) => ({
                ...prev,
                character: null,
            }));

            console.log(
                "[AD VIDEO AI] Character image updated:",
                imageUrl
            );

            return;
        }

        /*
         * LOCATION
         */
        if (imageType === "location") {
            setLocationImage(imageUrl);

            setGeneratingLocation(false);

            setImageGeneration((prev) => ({
                ...prev,
                location: null,
            }));

            console.log(
                "[AD VIDEO AI] Location image updated:",
                imageUrl
            );

            return;
        }
    };

    useEffect(() => {

        const handleBackendCompleted = (event) => {
            const data = event?.detail;

            console.group(
                "%c[AD VIDEO AI] BACKEND COMPLETED EVENT",
                "color:#22c55e;font-weight:700;"
            );

            console.log("Event:", data);

            console.groupEnd();

            const completedTransactionId =
                data?.transactionId ||
                data?.data?.transactionId;

            const characterTransactionId =
                imageGeneration?.character?.transactionId;

            const locationTransactionId =
                imageGeneration?.location?.transactionId;

            if (
                completedTransactionId &&
                (
                    completedTransactionId ===
                    characterTransactionId ||
                    completedTransactionId ===
                    locationTransactionId
                )
            ) {
                handleImageCompleted(data);
                return;
            }

            /*
             * =====================================================
             * ADVERTISEMENT VIDEO AI COMPLETED
             * =====================================================
             */

            if (
                data?.type ===
                "advertisement-video-ai" &&
                // data?.action ===
                // "ad_video_ai_completed" &&
                data?.action === "ad_video_ai_image_completed" &&
                data?.status === "completed"
            ) {
                handleCompleted(
                    data,
                    "websocket/event"
                );
            }
        };

        const handleBackendFailed = (event) => {
            const data = event?.detail;

            console.group(
                "%c[AD VIDEO AI] BACKEND FAILED EVENT",
                "color:#ef4444;font-weight:700;"
            );
            console.log(
                "Event:",
                data
            );
            console.groupEnd();

            if (
                data?.type ===
                "advertisement-video-ai" &&
                data?.action ===
                "ad_video_ai_failed" &&
                data?.status === "failed"
            ) {
                handleFailed(data);
            }
        };

        window.addEventListener(
            "backend_task_completed",
            handleBackendCompleted
        );

        window.addEventListener(
            "backend_task_failed",
            handleBackendFailed
        );

        return () => {
            window.removeEventListener(
                "backend_task_completed",
                handleBackendCompleted
            );

            window.removeEventListener(
                "backend_task_failed",
                handleBackendFailed
            );
        };
    }, [transactionId, imageGeneration]);

    // =====================================================
    // BROWSER WAKE UP
    // =====================================================

    // useEffect(() => {
    //     const handleWakeUp = () => {
    //         if (
    //             document.visibilityState ===
    //             "visible" ||
    //             !document.hidden
    //         ) {
    //             console.log(
    //                 "[AD VIDEO AI] Browser active again → checking status..."
    //             );

    //             checkActiveStatus();
    //         }
    //     };

    //     document.addEventListener(
    //         "visibilitychange",
    //         handleWakeUp
    //     );

    //     window.addEventListener(
    //         "focus",
    //         handleWakeUp
    //     );

    //     window.addEventListener(
    //         "pageshow",
    //         handleWakeUp
    //     );

    //     return () => {
    //         document.removeEventListener(
    //             "visibilitychange",
    //             handleWakeUp
    //         );

    //         window.removeEventListener(
    //             "focus",
    //             handleWakeUp
    //         );

    //         window.removeEventListener(
    //             "pageshow",
    //             handleWakeUp
    //         );
    //     };
    // }, []);


    useEffect(() => {
        const hasActiveTransaction = () => {
            try {
                const raw = localStorage.getItem(
                    ACTIVE_STORAGE_KEY
                );

                if (!raw) return false;

                const active = JSON.parse(raw);

                return Boolean(
                    active?.transactionId &&
                    active?.aiType === "advertisement-video-ai"
                );
            } catch (error) {
                console.error(
                    "[AD VIDEO AI] Active transaction check failed:",
                    error
                );

                return false;
            }
        };

        const handleWakeUp = () => {
            // Never start a status check just because
            // the user tapped a suggestion or focused an input.
            if (document.visibilityState !== "visible") {
                return;
            }

            // Resume polling only for an existing transaction.
            if (!hasActiveTransaction()) {
                return;
            }

            console.log(
                "[AD VIDEO AI] Resuming existing transaction..."
            );

            checkActiveStatus();
        };

        document.addEventListener(
            "visibilitychange",
            handleWakeUp
        );

        window.addEventListener("focus", handleWakeUp);
        window.addEventListener("pageshow", handleWakeUp);

        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleWakeUp
            );

            window.removeEventListener(
                "focus",
                handleWakeUp
            );

            window.removeEventListener(
                "pageshow",
                handleWakeUp
            );
        };
    }, []);


    useEffect(() => {
        return () => {
            if (promptTimeoutRef.current) {
                clearTimeout(promptTimeoutRef.current);
            }
        };
    }, []);




    // For Temporary Debugging: Log state changes to console for easier tracking of issues. Remove in production.
    useEffect(() => {
        console.log("[AD VIDEO DEBUG]", {
            isLoading,
            checkingPrompt,
            transactionId,
            sessionId,
            time: new Date().toISOString(),
        });
    }, [
        isLoading,
        checkingPrompt,
        transactionId,
        sessionId,
    ]);


    const hasAnyInput = Object.values(
        formData
    ).some((value) => value.trim());

    return (
        <Fragment>
            {/* <PageHeading
                title="AD VIDEO AI"
                description="Transform your product or service idea into a complete professional advertisement blueprint with AI."
            /> */}

            <div className="advideo-page">
                <div className="container">
                    {/* =====================================================
                        CREDITS
                    ===================================================== */}
                    <div className="d-flex align-items-center justify-content-center centralize-credits-btn-sm mt-2">
                        <div>
                            <p className="all-page-title all-page-gradient-text text-center my-4">AD VIDEO AI</p>
                            <div className="credits-actions">

                                <button className="credits-button">
                                    CREDITS : {creditToLocalString}
                                </button>

                                <button
                                    className="buy-credits-button"
                                    onClick={() => navigate("/purchase")}
                                >
                                    BUY CREDITS
                                </button>

                                <button
                                    onClick={() => navigate("/usage")}
                                    className="money-exchange-button"
                                >
                                    <img src={moneyExchangeIcon} alt="usage" />
                                </button>

                                <button
                                    type="button"
                                    className="money-exchange-button"
                                    onClick={() => {
                                        // setShowPdfModal(true);
                                        swal({
                                            title: "Coming Soon",
                                            text: "Sample video feature is coming soon.",
                                            // icon: sampleIcon,
                                            button: "Okay",
                                            className:
                                                "modal_class_success",
                                        });
                                    }}

                                >
                                    <img src={sampleIcon} alt="See sample video" />
                                </button>

                            </div>
                        </div>
                    </div>


                    {/* =====================================================
                        INTRO
                    ===================================================== */}

                    <div className="advideo-intro mt-5 ">
                        <h1>
                            Build Your Ad
                            <br />
                            <span className="gradiant-text" >
                                From Idea to Production
                            </span>
                        </h1>

                        <p>
                            Tell us about your product,
                            audience and message. AD VIDEO AI will
                            create a complete advertisement
                            blueprint including characters,
                            locations, story and production
                            prompts.
                        </p>
                    </div>

                    {/* =====================================================
                        FORM
                    ===================================================== */}

                    <div className="advideo-form-card">
                        <div className="advideo-form-header">
                            <div>
                                <span>
                                    STEP-BY-STEP
                                </span>
                            </div>

                            <button
                                type="button"
                                className="advideo-clear-link"
                                onClick={
                                    handleClear
                                }
                                disabled={
                                    !hasAnyInput ||
                                    isLoading
                                }
                            >
                                Clear All
                            </button>
                        </div>

                        <div className="advideo-form-grid">
                            {formQuestions.map(
                                (question, index) => (
                                    <div
                                        className={`advideo-form-group ${question.isTextarea
                                            ? "full"
                                            : ""
                                            }`}
                                        key={
                                            question.key
                                        }
                                    >
                                        <div className="advideo-label-row">
                                            <div className="advideo-step-number">
                                                {String(
                                                    index +
                                                    1
                                                ).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </div>

                                            <label>
                                                {
                                                    question.label
                                                }

                                                {question.required ? (
                                                    <span className="required">
                                                        *
                                                    </span>
                                                ) : (
                                                    <span className="optional">
                                                        Optional
                                                    </span>
                                                )}
                                            </label>
                                        </div>

                                        {question.isTextarea ? (
                                            <textarea
                                                className="advideo-input"
                                                rows="4"
                                                placeholder={
                                                    question.placeholder
                                                }
                                                value={
                                                    formData[
                                                    question
                                                        .key
                                                    ]
                                                }
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    handleInputChange(
                                                        question.key,
                                                        value
                                                    );
                                                    if (question.key === "promoting") {
                                                        if (promptTimeoutRef.current) {
                                                            clearTimeout(promptTimeoutRef.current);
                                                        }
                                                        promptTimeoutRef.current = setTimeout(() => {
                                                            checkPromotingPrompt(value);
                                                        }, 1500);
                                                    }
                                                }}
                                                disabled={
                                                    isLoading
                                                }
                                            />
                                        ) : (
                                            <>
                                                <input
                                                    type="text"
                                                    className="advideo-input"
                                                    placeholder={
                                                        question.placeholder
                                                    }
                                                    value={
                                                        formData[
                                                        question
                                                            .key
                                                        ]
                                                    }
                                                    onChange={(
                                                        e
                                                    ) =>
                                                        handleInputChange(
                                                            question.key,
                                                            e
                                                                .target
                                                                .value
                                                        )
                                                    }
                                                    disabled={
                                                        isLoading
                                                    }
                                                />

                                                {question.key === "promoting" && (
                                                    <>
                                                        {checkingPrompt && (
                                                            <div className="d-flex align-items-center mt-3 mb-2">
                                                                <div
                                                                    className="spinner-border text-light spinner-border-sm me-2"
                                                                    role="status"
                                                                >
                                                                    <span className="visually-hidden">
                                                                        Loading...
                                                                    </span>
                                                                </div>

                                                                <span
                                                                    className="text-warning"
                                                                    style={{
                                                                        marginLeft: "8px",
                                                                        fontSize: "10px"
                                                                    }}
                                                                >
                                                                    Generating suggestions...
                                                                </span>
                                                            </div>
                                                        )}

                                                        {
                                                            !checkingPrompt &&

                                                            <div
                                                                className="mt-3 mb-2"
                                                                style={{
                                                                    fontSize: "13px",
                                                                    color: "#a0a0a0",
                                                                }}
                                                            >
                                                                Suggested:{" "}
                                                                {/* <span
                                                                    style={{
                                                                        color: "#4da8da",
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                    }}
                                                                    // onClick={() => {
                                                                    //     handleInputChange(
                                                                    //         "promoting",
                                                                    //         suggestedPrompt
                                                                    //     );

                                                                    //     if (promptTimeoutRef.current) {
                                                                    //         clearTimeout(
                                                                    //             promptTimeoutRef.current
                                                                    //         );
                                                                    //     }

                                                                    //     checkPromotingPrompt(
                                                                    //         suggestedPrompt
                                                                    //     );
                                                                    // }}
                                                                    onClick={() => {
                                                                        const value = suggestedPrompt?.trim();

                                                                        if (!value) return;

                                                                        handleInputChange("promoting", value);

                                                                        if (promptTimeoutRef.current) {
                                                                            clearTimeout(promptTimeoutRef.current);
                                                                            promptTimeoutRef.current = null;
                                                                        }

                                                                        checkPromotingPrompt(value);
                                                                    }}
                                                                >
                                                                    {suggestedPrompt}
                                                                </span> */}


                                                                <button
                                                                    type="button"
                                                                    className="advideo-suggested-prompt"
                                                                    onClick={(event) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();

                                                                        const value = String(suggestedPrompt || "").trim();

                                                                        if (!value) return;

                                                                        if (promptTimeoutRef.current) {
                                                                            clearTimeout(promptTimeoutRef.current);
                                                                            promptTimeoutRef.current = null;
                                                                        }

                                                                        handleInputChange("promoting", value);

                                                                        // This only checks the prompt.
                                                                        // It must never start advertisement generation.
                                                                        checkPromotingPrompt(value);
                                                                    }}
                                                                >
                                                                    {suggestedPrompt}
                                                                </button>

                                                            </div>
                                                        }
                                                    </>
                                                )}
                                            </>
                                        )}


                                        {backendSuggestions &&
                                            backendSuggestions[question.key] && (
                                                <div
                                                    onClick={() =>
                                                        handleInputChange(
                                                            question.key,
                                                            backendSuggestions[question.key]
                                                        )
                                                    }
                                                    style={{
                                                        cursor: "pointer",
                                                        background: "#011225",
                                                        padding: "12px",
                                                        borderRadius: "10px",
                                                        marginTop: "12px",
                                                        marginBottom: "12px",
                                                        border: "1px solid #918888",
                                                    }}
                                                >
                                                    <p
                                                        className="mb-1 text-warning"
                                                        style={{ fontSize: "14px" }}
                                                    >
                                                        Suggested Idea:
                                                    </p>

                                                    <p
                                                        className="mb-0 text-white"
                                                        style={{ fontSize: "14px" }}
                                                    >
                                                        {backendSuggestions[question.key]}
                                                    </p>
                                                </div>
                                            )}
                                    </div>
                                )
                            )}
                        </div>

                        <div className="advideo-form-footer">
                            <div className="advideo-footer-note">
                                <span>✦</span>
                                AD VIDEO AI will generate a
                                production-ready
                                advertisement structure.
                            </div>

                            <Button
                                variant="contained"
                                onClick={
                                    handleGenerate
                                }
                                disabled={
                                    !isFormValid ||
                                    isLoading
                                }
                                className="advideo-generate-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        GENERATING
                                    </>
                                ) : (
                                    <>
                                        GENERATE
                                        ADVERTISEMENT
                                        <span>
                                            →
                                        </span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                    {/* =====================================================
                        LOADING
                    ===================================================== */}

                    {isLoading && (
                        <>
                            {/* <AiFactsDisplay /> */}

                            <LoadingState
                                transactionId={
                                    transactionId
                                }
                            />
                        </>
                    )}

                    {/* =====================================================
                        EMPTY
                    ===================================================== */}

                    {!isLoading &&
                        !resultData && (
                            <EmptyState />
                        )}

                    {/* =====================================================
                        RESULT
                    ===================================================== */}

                    {!isLoading &&
                        resultData && (
                            <ResultDashboard
                                resultData={resultData}
                                onGenerateBibleImage={handleGenerateBibleImage}
                                generatingCharacter={generatingCharacter}
                                generatingLocation={generatingLocation}
                                transactionId={transactionId}
                                characterImage={characterImage}
                                locationImage={locationImage}
                            />
                        )}

                    {/* =====================================================
                        SESSIONS
                    ===================================================== */}
                </div>
            </div>
        </Fragment>
    );
}